package fr.manyverse.app.presentation.components.notification

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.draggable
import androidx.compose.foundation.gestures.rememberDraggableState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Backpack
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.item_sender_game_master
import manyverse.shared.generated.resources.notification_item_received
import org.jetbrains.compose.resources.stringResource
import kotlin.math.abs

@Composable
internal fun ItemReceivedNotification(
    notification: NotificationContent.ItemReceived,
    onDismiss: () -> Unit,
    onClick: () -> Unit
) {
    var progress by remember(notification.id) { mutableFloatStateOf(1f) }
    val offsetX = remember(notification.id) { Animatable(0f) }
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(notification.id) {
        val steps = 100
        val stepDelay = NOTIFICATION_DURATION_MS / steps
        for (i in steps downTo 0) {
            progress = i.toFloat() / steps
            delay(stepDelay)
        }
    }

    val draggableState = rememberDraggableState { delta ->
        coroutineScope.launch { offsetX.snapTo(offsetX.value + delta) }
    }

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 8.dp,
        modifier = Modifier
            .fillMaxWidth()
            .graphicsLayer { translationX = offsetX.value }
            .draggable(
                orientation = Orientation.Horizontal,
                state = draggableState,
                onDragStopped = {
                    if (abs(offsetX.value) > SWIPE_DISMISS_THRESHOLD) {
                        val target = if (offsetX.value > 0) SWIPE_OUT_TARGET else -SWIPE_OUT_TARGET
                        offsetX.animateTo(target)
                        onDismiss()
                    } else {
                        offsetX.animateTo(0f)
                    }
                }
            )
            .clickable(onClick = onClick)
    ) {
        Column {
            Row(
                modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    shape = CircleShape,
                    color = MaterialTheme.colorScheme.primaryContainer,
                    modifier = Modifier.size(34.dp)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            imageVector = Icons.Default.Backpack,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onPrimaryContainer,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                }

                Spacer(modifier = Modifier.width(10.dp))

                // Le nom cède la place au reste de la ligne : tronqué plutôt que d'écraser la suite.
                Text(
                    text = notification.senderName ?: stringResource(Res.string.item_sender_game_master),
                    fontWeight = FontWeight.Bold,
                    fontSize = 13.sp,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f, fill = false)
                )

                Text(
                    text = " ${stringResource(Res.string.notification_item_received)}",
                    fontSize = 13.sp,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            LinearProgressIndicator(
                progress = { progress },
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.primary,
                trackColor = Color.Transparent
            )
        }
    }
}
